/**
 * Axios service layer for the Managed-Service dashboard. Calls the ECM webapi
 * `api/v3/managed-service/*` (cookie auth); ECM proxies these to the AI tool and
 * scopes/authorizes server-side. Query keys match the webapi's camelCase params.
 *
 * Uses the no-interceptor axios instance on purpose: this surface is flat /
 * account-level (TSA), not company-scoped, so it must bypass the global request
 * interceptor that injects `/companies/{activeCompanyId}/` into `/api/v3/*` URLs
 * (same reason iam-enablement / settings / role-management use it). Errors are
 * handled locally by the MS slices rather than the global error boundary.
 */

import axiosWithoutInterceptor from "../../../axiosWithoutInterceptor";
import { buildApiV3Url } from "../../../shared/Utils";
import type {
    ImportResult,
    MsAccount,
    MsAccountsPage,
    MsAgreement,
    MsCertDetail,
    MsCertFilters,
    MsCertList,
    MsCertPipeline,
    MsCompany,
    MsCompleteOnVerdictResult,
    MsCost,
    MsDiagnosticsCertFilters,
    MsDiagnosticsCerts,
    MsDiagnosticsFields,
    MsDiagnosticsFilters,
    MsDiagnosticsGroupBy,
    MsDiagnosticsGroups,
    MsDiagnosticsGroupsTimeline,
    MsDiagnosticsReasons,
    MsDiagnosticsSummary,
    MsDispatchResult,
    MsEcmCertList,
    MsFilters,
    MsForm,
    MsFormDetail,
    MsFormEnablement,
    MsFormsPage,
    MsFormsQuery,
    MsQuality,
    MsRuleDocument,
    MsRuleDraftSaved,
    MsRulePack,
    MsRulePublished,
    MsRuleSet,
    MsRuleValidation,
    MsCandidateTemplate,
    MsRuleDiff,
    MsRuleVersion,
    MsSingleRule,
    MsSellerAliasDiff,
    MsSellerAliasDocument,
    MsSellerAliasPackSummary,
    MsSellerAliasValidation,
    MsSellerAliasVersion,
    MsSendToAiResult,
    MsSummary,
    MsSyncResult,
    MsTrend,
    MsWorklistSummary,
} from "../types";

const BASE = "managed-service";
const requestConfig = { withCredentials: true } as const;

type QueryValue = string | number | undefined | null;

const buildQuery = (
    params: Record<string, QueryValue>,
    repeats?: Record<string, Array<string | number>>
): string => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            query.append(key, String(value));
        }
    });
    Object.entries(repeats ?? {}).forEach(([key, values]) => {
        (values ?? []).forEach(value => {
            if (value !== undefined && value !== null && value !== "") {
                query.append(key, String(value));
            }
        });
    });
    const serialized = query.toString();
    return serialized ? `?${serialized}` : "";
};

// The scalar (single-value) query params from a filter set — `statuses` / multi account+form
// lists are repeated params, handled separately.
const scalarFilters = (filters: MsFilters): Record<string, QueryValue> => ({
    accountId: filters.accountId,
    companyId: filters.companyId,
    since: filters.since,
    until: filters.until,
    formPack: filters.formPack,
});

const metricsUrl = (resource: string, filters: MsFilters): string =>
    buildApiV3Url(
        `${BASE}/metrics/${resource}${buildQuery(scalarFilters(filters), {
            accountIds: filters.accountIds ?? [],
            formPacks: filters.formPacks ?? [],
        })}`
    );
export const getSummary = (filters: MsFilters): Promise<MsSummary> =>
    axiosWithoutInterceptor
        .get<MsSummary>(metricsUrl("summary", filters), requestConfig)
        .then(response => response.data);

export const getAgreement = (filters: MsFilters): Promise<MsAgreement> =>
    axiosWithoutInterceptor
        .get<MsAgreement>(metricsUrl("agreement", filters), requestConfig)
        .then(response => response.data);

export const getQuality = (filters: MsFilters): Promise<MsQuality> =>
    axiosWithoutInterceptor
        .get<MsQuality>(metricsUrl("quality", filters), requestConfig)
        .then(response => response.data);

export const getCost = (filters: MsFilters): Promise<MsCost> =>
    axiosWithoutInterceptor
        .get<MsCost>(metricsUrl("cost", filters), requestConfig)
        .then(response => response.data);

export const getTrend = (filters: MsFilters): Promise<MsTrend> =>
    axiosWithoutInterceptor
        .get<MsTrend>(metricsUrl("trend", filters), requestConfig)
        .then(response => response.data);

/**
 * Per-form-pack Complete-on-verdict state (T16 / burn-up banked %). Enablement is global (D8):
 * callers still pass filters so the signature matches other MS loads, but the wire call is
 * unfiltered — CUP groups AV templates by formPack (`GET …/forms/enablement`).
 */
// The parameter is deliberate (see above): it keeps this loader's signature aligned with
// the other MS loaders, while the wire call stays unfiltered.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getFormEnablement = (_filters: MsFilters): Promise<MsFormEnablement> =>
    axiosWithoutInterceptor
        .get<MsFormEnablement>(buildApiV3Url(`${BASE}/forms/enablement`), requestConfig)
        .then(response => response.data);

const diagnosticsUrl = (
    resource: string,
    filters: MsDiagnosticsFilters,
    params: Record<string, QueryValue> = {},
    repeats: Record<string, Array<string | number>> = {}
): string =>
    buildApiV3Url(
        `${BASE}/metrics/diagnostics/${resource}${buildQuery(
            {
                since: filters.since,
                until: filters.until,
                ...params,
            },
            {
                accountIds: filters.accountIds,
                formPacks: filters.formPacks,
                ...repeats,
            }
        )}`
    );

export const getDiagnosticsSummary = (
    filters: MsDiagnosticsFilters
): Promise<MsDiagnosticsSummary> =>
    axiosWithoutInterceptor
        .get<MsDiagnosticsSummary>(diagnosticsUrl("summary", filters), requestConfig)
        .then(response => response.data);

export const getDiagnosticsFields = (filters: MsDiagnosticsFilters): Promise<MsDiagnosticsFields> =>
    axiosWithoutInterceptor
        .get<MsDiagnosticsFields>(diagnosticsUrl("fields", filters), requestConfig)
        .then(response => response.data);

export const getDiagnosticsReasons = (
    filters: MsDiagnosticsFilters
): Promise<MsDiagnosticsReasons> =>
    axiosWithoutInterceptor
        .get<MsDiagnosticsReasons>(diagnosticsUrl("reasons", filters), requestConfig)
        .then(response => response.data);

export const getDiagnosticsGroups = (
    filters: MsDiagnosticsFilters & {
        groupBy: MsDiagnosticsGroupBy;
        page?: number;
        pageSize?: number;
        /** AV groups allow-listed field; default processed. */
        sortBy?: string;
        /** asc | desc; default desc. */
        order?: "asc" | "desc";
        /** When false, AV omits the full-cohort totals row. Default true. */
        includeTotals?: boolean;
    }
): Promise<MsDiagnosticsGroups> =>
    axiosWithoutInterceptor
        .get<MsDiagnosticsGroups>(
            diagnosticsUrl("groups", filters, {
                groupBy: filters.groupBy,
                page: filters.page,
                pageSize: filters.pageSize,
                sortBy: filters.sortBy,
                order: filters.order,
                includeTotals:
                    filters.includeTotals === undefined
                        ? undefined
                        : filters.includeTotals
                          ? "true"
                          : "false",
            }),
            requestConfig
        )
        .then(response => response.data);

// 22 form packs today (§ Per-form data, 02-api-contract.md). Comfortably covers the full set in
// one page; if it is ever exceeded, `getFormMetricsGroups` throws rather than silently truncating.
const FORM_GROUPS_PAGE_SIZE = 100;

/**
 * Per-form decided volume and agreement matrix for the burn-up tab. Reuses `getDiagnosticsGroups`
 * — there is no dedicated `getByForm` call and no second `getAgreement` call; the confusion
 * matrix travels in the same row as the decided volume (§ Per-form data, 02-api-contract.md).
 *
 * Requests a page sized to cover every form pack and asserts the response was not truncated: a
 * short page would understate `qualified`, which is a wrong number, not a partial one.
 */
export const getFormMetricsGroups = (filters: MsDiagnosticsFilters): Promise<MsDiagnosticsGroups> =>
    getDiagnosticsGroups({
        ...filters,
        groupBy: "form",
        pageSize: FORM_GROUPS_PAGE_SIZE,
        includeTotals: true,
    }).then(groups => {
        if (groups.rows.length !== groups.total) {
            throw new Error(
                `getFormMetricsGroups: received ${groups.rows.length} of ${groups.total} form ` +
                    "packs. Increase pageSize or add pagination — a truncated form list " +
                    "understates qualified, which is a wrong number, not a partial one."
            );
        }
        return groups;
    });

/**
 * Per-form-pack counters at N caller-chosen bucket cutoffs, in one call (GAP-5). Replaces the
 * N-call sweep `eligibilityHistory.ts` used to make against `getFormMetricsGroups` — see that
 * file and `docs/specs/burn-up-tab/02-api-contract.md`'s GAP-5 section (tasks.md T17).
 *
 * `buckets` are positionally paired `bucketKey`/`bucketUntil` query params (repeated, ascending,
 * last `bucketUntil` equal to `filters.until`) — the same repeated-param style already used for
 * `accountIds`/`formPacks`. Bucket boundaries stay client-owned (`Overview/trendBuckets.ts`'s
 * `bucketsFor`); the endpoint treats `bucketKey` as opaque and only echoes it back.
 */
export const getFormMetricsGroupsTimeline = (
    filters: MsDiagnosticsFilters,
    buckets: Array<{ key: string; until: string }>
): Promise<MsDiagnosticsGroupsTimeline> =>
    axiosWithoutInterceptor
        .get<MsDiagnosticsGroupsTimeline>(
            diagnosticsUrl(
                "groups/timeline",
                filters,
                {},
                {
                    bucketKey: buckets.map(bucket => bucket.key),
                    bucketUntil: buckets.map(bucket => bucket.until),
                }
            ),
            requestConfig
        )
        .then(response => response.data);

export const getDiagnosticsCerts = (
    filters: MsDiagnosticsCertFilters
): Promise<MsDiagnosticsCerts> =>
    axiosWithoutInterceptor
        .get<MsDiagnosticsCerts>(
            diagnosticsUrl("certs", filters, {
                groupBy: filters.groupBy,
                metric: filters.metric,
                reasonMismatchMode: filters.reasonMismatchMode,
                field: filters.field,
                reasonCode: filters.reasonCode,
                reasonState: filters.reasonState,
                page: filters.page,
                pageSize: filters.pageSize,
            }),
            requestConfig
        )
        .then(response => response.data);

// Grid column id (camelCase) -> the tool's allow-listed sort column (snake_case).
const CERT_SORT_KEYS: Record<string, string> = {
    ecmCertificateId: "ecm_certificate_id",
    formPack: "form_pack",
    verdict: "verdict",
    evaluatedAt: "evaluated_at",
    finalStatus: "final_status",
};

/**
 * Map UI sort state to API sortDir.
 * `evaluatedAt` uses Age semantics: UI ascending = youngest/newest first, so the
 * underlying timestamp is requested descending. Other columns: ascending → asc.
 */
export const toCertSortDir = (sortColumn?: [string, boolean]): "asc" | "desc" | undefined => {
    if (!sortColumn) {
        return undefined;
    }
    const ascending = sortColumn[1];
    if (sortColumn[0] === "evaluatedAt") {
        return ascending ? "desc" : "asc";
    }
    return ascending ? "asc" : "desc";
};

const toSortDir = (sortColumn?: [string, boolean]): "asc" | "desc" | undefined =>
    toCertSortDir(sortColumn);

export const getCerts = (
    filters: MsFilters,
    verdicts: string[],
    page: number,
    pageSize: number,
    sortColumn?: [string, boolean]
): Promise<MsCertList> => {
    const sort = sortColumn ? CERT_SORT_KEYS[sortColumn[0]] : undefined;
    const sortDir = toCertSortDir(sortColumn);
    return axiosWithoutInterceptor
        .get<MsCertList>(
            buildApiV3Url(
                `${BASE}/certs${buildQuery(
                    {
                        ...scalarFilters(filters),
                        validatedOn: filters.validatedOn,
                        sort,
                        sortDir,
                        page,
                        pageSize,
                    },
                    {
                        verdict: verdicts,
                        humanOutcome: filters.humanOutcomes ?? [],
                        certificateIds: filters.certificateIds ?? [],
                    }
                )}`
            ),
            requestConfig
        )
        .then(response => response.data);
};

export const getWorklistSummary = (filters: MsFilters): Promise<MsWorklistSummary> =>
    axiosWithoutInterceptor
        .get<MsWorklistSummary>(
            buildApiV3Url(
                `${BASE}/metrics/worklist-summary${buildQuery(
                    {
                        ...scalarFilters(filters),
                        validatedOn: filters.validatedOn,
                    },
                    {
                        verdict: filters.statuses ?? [],
                        humanOutcome: filters.humanOutcomes ?? [],
                        certificateIds: filters.certificateIds ?? [],
                    }
                )}`
            ),
            requestConfig
        )
        .then(response => response.data);

export const getCertDetail = (
    ecmCertificateId: string,
    accountId?: string
): Promise<MsCertDetail> =>
    axiosWithoutInterceptor
        .get<MsCertDetail>(
            buildApiV3Url(
                `${BASE}/metrics/certs/${encodeURIComponent(ecmCertificateId)}${buildQuery({ accountId })}`
            ),
            requestConfig
        )
        .then(response => response.data);

export const getCertPipeline = (
    ecmCertificateId: string,
    accountId?: string
): Promise<MsCertPipeline> =>
    axiosWithoutInterceptor
        .get<MsCertPipeline>(
            buildApiV3Url(
                `${BASE}/metrics/certs/${encodeURIComponent(ecmCertificateId)}/pipeline${buildQuery({ accountId })}`
            ),
            requestConfig
        )
        .then(response => response.data);

export const getAccounts = (): Promise<MsAccount[]> =>
    axiosWithoutInterceptor
        .get<MsAccount[]>(buildApiV3Url(`${BASE}/accounts`), requestConfig)
        .then(response => response.data);

const FORM_SORT_KEYS: Record<string, string> = {
    id: "id",
    formCode: "form_code",
    stateCode: "state_code",
    title: "title",
    formPack: "form_pack",
    enabled: "enabled",
    updatedAt: "updated_at",
    processEnabled: "process",
    populateEnabled: "send_verdict_back",
    completeEnabled: "complete_on_verdict",
    updatedBy: "mode_updated_by",
};

export const getFormsPage = (query: MsFormsQuery): Promise<MsFormsPage> =>
    axiosWithoutInterceptor
        .get<MsFormsPage>(
            buildApiV3Url(
                `${BASE}/forms${buildQuery({
                    page: query.page,
                    pageSize: query.pageSize,
                    formCode: query.formCode || undefined,
                    formName: query.formName || undefined,
                    stateCode: query.stateCode || undefined,
                    formPack: query.formPack || undefined,
                    sort: FORM_SORT_KEYS[query.sortColumn[0]],
                    sortDir: toSortDir(query.sortColumn),
                })}`
            ),
            requestConfig
        )
        .then(response => response.data);

export const getFormDetail = (templateId: number): Promise<MsFormDetail> =>
    axiosWithoutInterceptor
        .get<MsFormDetail>(
            buildApiV3Url(`${BASE}/forms/${encodeURIComponent(String(templateId))}`),
            requestConfig
        )
        .then(response => response.data);

export const patchFormMode = (
    templateId: number,
    runUntil: string,
    ecmWriteMode: string | null
): Promise<MsForm> =>
    axiosWithoutInterceptor
        .patch<MsForm>(
            buildApiV3Url(`${BASE}/forms/${encodeURIComponent(String(templateId))}/mode`),
            { runUntil, ecmWriteMode },
            requestConfig
        )
        .then(response => response.data);

// Grid column id -> wire sort key. The three flag columns sort too; `dispatch_enabled` is
// served by ECM itself (the flag is its driver column, not the tool's), the other two are
// delegated to the tool like the text columns.
const ACCOUNT_SORT_KEYS: Record<string, string> = {
    avataxAccountId: "avatax_account_id",
    accountName: "account_name",
    dispatchEnabled: "dispatch_enabled",
    syncEnabled: "sync_enabled",
    completeOnVerdict: "complete_on_verdict",
    updatedAt: "updated_at",
    updatedBy: "updated_by",
};

export const getAccountsPage = (
    page: number,
    pageSize: number,
    search: string,
    sortColumn: [string, boolean]
): Promise<MsAccountsPage> =>
    axiosWithoutInterceptor
        .get<MsAccountsPage>(
            buildApiV3Url(
                `${BASE}/accounts-page${buildQuery({
                    page,
                    pageSize,
                    search: search || undefined,
                    sort: ACCOUNT_SORT_KEYS[sortColumn[0]],
                    sortDir: toSortDir(sortColumn),
                })}`
            ),
            requestConfig
        )
        .then(response => response.data);

/** Companies (from ECM) enrolled under an account — options for the Worklist company filter. */
export const getCompanies = (accountId: string): Promise<MsCompany[]> =>
    axiosWithoutInterceptor
        .get<
            MsCompany[]
        >(buildApiV3Url(`${BASE}/accounts/${encodeURIComponent(accountId)}/companies`), requestConfig)
        .then(response => response.data);

/** Make the selected Managed-Service account/company the active TSA session context. */
/**
 * `signal` lets a superseded switch be aborted, so its context write never
 * reaches the server and cannot land after a newer one's.
 */
export const setManagedServiceContext = (
    accountId: string,
    companyId: string,
    signal?: AbortSignal
): Promise<void> =>
    axiosWithoutInterceptor
        .post(
            buildApiV3Url(`${BASE}/context`),
            { accountId: Number(accountId), companyId: Number(companyId) },
            { ...requestConfig, signal }
        )
        .then(() => undefined);

/** Distinct form-pack slugs (template catalog) — options for the form filter. */
export const getFormPacks = (): Promise<string[]> =>
    axiosWithoutInterceptor
        .get<{ formPacks: string[] }>(buildApiV3Url(`${BASE}/metrics/form-packs`), requestConfig)
        .then(response => response.data.formPacks ?? []);

export const importAccounts = (file: File): Promise<ImportResult> => {
    const form = new FormData();
    form.append("file", file);
    return axiosWithoutInterceptor
        .post<ImportResult>(buildApiV3Url(`${BASE}/accounts:import`), form, requestConfig)
        .then(response => response.data);
};

export const setSync = (accountId: string, syncEnabled: boolean): Promise<MsSyncResult> =>
    axiosWithoutInterceptor
        .put<MsSyncResult>(
            buildApiV3Url(`${BASE}/accounts/${encodeURIComponent(accountId)}/sync`),
            { syncEnabled },
            requestConfig
        )
        .then(response => response.data);

export const setCompleteOnVerdict = (
    accountId: string,
    completeOnVerdict: boolean
): Promise<MsCompleteOnVerdictResult> =>
    axiosWithoutInterceptor
        .put<MsCompleteOnVerdictResult>(
            buildApiV3Url(`${BASE}/accounts/${encodeURIComponent(accountId)}/complete-on-verdict`),
            { completeOnVerdict },
            requestConfig
        )
        .then(response => response.data);

export const setDispatch = (accountId: string, enabled: boolean): Promise<MsDispatchResult> =>
    axiosWithoutInterceptor
        .put<MsDispatchResult>(
            buildApiV3Url(`${BASE}/accounts/${encodeURIComponent(accountId)}/dispatch`),
            { enabled },
            requestConfig
        )
        .then(response => response.data);

/** Certificates tab: an account+company's ECM certs, filtered and paged server-side. */
const ECM_CERT_SORT_KEYS: Record<string, string> = {
    certificateId: "certificate_id",
    certificateNumber: "certificate_number",
    status: "status",
    exposureZone: "exposure_zone",
    formPack: "form_pack",
    valid: "valid",
    agenticStatus: "agentic_status",
    created: "created",
    expirationDate: "expiration_date",
};

export interface GetEcmCertificatesParams {
    accountId: string;
    companyId: string;
    statuses?: string[];
    certificateIds?: number[];
    agenticStatuses?: string[];
    page: number;
    pageSize: number;
    regionIds?: number[];
    reasonIds?: number[];
    formPacks?: string[];
    sortColumn?: [string, boolean];
}

export const getEcmCertificates = ({
    accountId,
    companyId,
    statuses = [],
    certificateIds = [],
    agenticStatuses = [],
    page,
    pageSize,
    regionIds = [],
    reasonIds = [],
    formPacks = [],
    sortColumn = ["created", false],
}: GetEcmCertificatesParams): Promise<MsEcmCertList> =>
    axiosWithoutInterceptor
        .get<MsEcmCertList>(
            buildApiV3Url(
                `${BASE}/ecm-certificates${buildQuery(
                    {
                        accountId,
                        companyId,
                        page,
                        pageSize,
                        sort: ECM_CERT_SORT_KEYS[sortColumn[0]],
                        sortDir: toSortDir(sortColumn),
                    },
                    {
                        status: statuses,
                        certificateIds,
                        agenticStatus: agenticStatuses,
                        regionIds,
                        reasonIds,
                        formPack: formPacks,
                    }
                )}`
            ),
            requestConfig
        )
        .then(response => response.data);

/** Region/Reason options for the account plus distinct Form packs for the company. */
export const getCertFilterOptions = (
    accountId: string,
    companyId: string
): Promise<MsCertFilters> =>
    axiosWithoutInterceptor
        .get<MsCertFilters>(
            buildApiV3Url(
                `${BASE}/accounts/${encodeURIComponent(accountId)}/cert-filters${buildQuery({
                    companyId,
                })}`
            ),
            requestConfig
        )
        .then(response => ({
            regions: response.data.regions ?? [],
            reasons: response.data.reasons ?? [],
            formPacks: response.data.formPacks ?? [],
        }));

/**
 * Send certificates to the AI dispatch queue. One endpoint for one or many — the ids travel in
 * the body, so the per-row button and a grid selection share a single contract.
 *
 * A 200 does not mean everything was sent: the backend reports an ineligible certificate as a
 * `dispatched: false` row rather than failing the request, so callers must read `results`.
 */
export const sendCertsToAi = (
    certificateIds: number[],
    accountId: string,
    companyId: string
): Promise<MsSendToAiResult> =>
    axiosWithoutInterceptor
        .post<MsSendToAiResult>(
            buildApiV3Url(`${BASE}/ecm-certificates:send-to-ai`),
            { accountId, companyId, certificateIds },
            requestConfig
        )
        .then(response => response.data);

/* ── Validation rules ──────────────────────────────────────────────────── */

export const getRulePacks = (): Promise<MsRulePack[]> =>
    axiosWithoutInterceptor
        .get<MsRulePack[]>(buildApiV3Url(`${BASE}/rules/packs`), requestConfig)
        .then(response => response.data ?? []);

export const getRuleSet = (params: {
    formPack?: string;
    companyPack?: string;
}): Promise<MsRuleSet> =>
    axiosWithoutInterceptor
        .get<MsRuleSet>(
            buildApiV3Url(
                `${BASE}/rules${buildQuery({
                    formPack: params.formPack,
                    companyPack: params.companyPack,
                })}`
            ),
            requestConfig
        )
        .then(response => response.data);

/**
 * One layer's own rules, unmerged — what the editing view lists.
 *
 * Not `getRuleSet`. That answers "what does a certificate in this scope get",
 * which resolves overrides and drops rules that do not apply, so a rule limited
 * to particular forms would be missing unless one of those forms happened to be
 * selected. An editing screen has to show the pack's contents, whole.
 */
export const getLayerRules = (packId: string): Promise<MsRuleSet> =>
    axiosWithoutInterceptor
        .get<MsRuleSet>(
            buildApiV3Url(`${BASE}/rules/packs/${encodeURIComponent(packId)}/rules-view`),
            requestConfig
        )
        .then(response => response.data);

/* Editing a pack: load its document, save a draft, have a different
   administrator publish it. The tool refuses anything that would not load, and
   refuses a publish by the draft's own author — this layer just carries it. */

export const getRuleDocument = (packId: string): Promise<MsRuleDocument> =>
    axiosWithoutInterceptor
        .get<MsRuleDocument>(
            buildApiV3Url(`${BASE}/rules/packs/${encodeURIComponent(packId)}/document`),
            requestConfig
        )
        .then(response => response.data);

export const getRuleHistory = (packId: string): Promise<MsRuleVersion[]> =>
    axiosWithoutInterceptor
        .get<
            MsRuleVersion[]
        >(buildApiV3Url(`${BASE}/rules/packs/${encodeURIComponent(packId)}/history`), requestConfig)
        .then(response => response.data ?? []);

export const validateRuleDocument = (
    packId: string,
    body: Record<string, unknown>
): Promise<MsRuleValidation> =>
    axiosWithoutInterceptor
        .post<MsRuleValidation>(
            buildApiV3Url(`${BASE}/rules/packs/${encodeURIComponent(packId)}/validate`),
            { body },
            requestConfig
        )
        .then(response => response.data);

export const saveRuleDraft = (
    packId: string,
    body: Record<string, unknown>,
    options: { note?: string; baseVersion?: number } = {}
): Promise<MsRuleDraftSaved> =>
    axiosWithoutInterceptor
        .put<MsRuleDraftSaved>(
            buildApiV3Url(`${BASE}/rules/packs/${encodeURIComponent(packId)}/draft`),
            { body, note: options.note, base_version: options.baseVersion },
            requestConfig
        )
        .then(response => response.data);

export const discardRuleDraft = (packId: string): Promise<void> =>
    axiosWithoutInterceptor
        .delete(
            buildApiV3Url(`${BASE}/rules/packs/${encodeURIComponent(packId)}/draft`),
            requestConfig
        )
        .then(() => undefined);

export const publishRuleDraft = (packId: string): Promise<MsRulePublished> =>
    axiosWithoutInterceptor
        .post<MsRulePublished>(
            buildApiV3Url(`${BASE}/rules/packs/${encodeURIComponent(packId)}/publish`),
            {},
            requestConfig
        )
        .then(response => response.data);

/* Editing one rule. Saves a draft of the whole pack with that rule replaced in
   place — same draft, same validation, same second-administrator approval. */

export const getSingleRule = (packId: string, ruleId: string): Promise<MsSingleRule> =>
    axiosWithoutInterceptor
        .get<MsSingleRule>(
            buildApiV3Url(
                `${BASE}/rules/packs/${encodeURIComponent(packId)}/rules/${encodeURIComponent(ruleId)}`
            ),
            requestConfig
        )
        .then(response => response.data);

export const validateSingleRule = (
    packId: string,
    ruleId: string,
    rule: Record<string, unknown>
): Promise<MsRuleValidation> =>
    axiosWithoutInterceptor
        .post<MsRuleValidation>(
            buildApiV3Url(
                `${BASE}/rules/packs/${encodeURIComponent(packId)}/rules/${encodeURIComponent(ruleId)}/validate`
            ),
            { rule },
            requestConfig
        )
        .then(response => response.data);

export const saveSingleRule = (
    packId: string,
    ruleId: string,
    rule: Record<string, unknown>,
    options: { note?: string; baseVersion?: number } = {}
): Promise<MsRuleDraftSaved> =>
    axiosWithoutInterceptor
        .put<MsRuleDraftSaved>(
            buildApiV3Url(
                `${BASE}/rules/packs/${encodeURIComponent(packId)}/rules/${encodeURIComponent(ruleId)}`
            ),
            { rule, note: options.note, base_version: options.baseVersion },
            requestConfig
        )
        .then(response => response.data);

/* Starting a pack for a form that has none. Creates a draft and routes the form
   to it; it reaches live certificates once a second administrator publishes. */

export const getCandidateTemplates = (): Promise<MsCandidateTemplate[]> =>
    axiosWithoutInterceptor
        .get<
            MsCandidateTemplate[]
        >(buildApiV3Url(`${BASE}/rules/candidate-templates`), requestConfig)
        .then(response => response.data ?? []);

export const createFormPack = (templateId: number, packId?: string): Promise<MsRuleDraftSaved> =>
    axiosWithoutInterceptor
        .post<MsRuleDraftSaved>(
            buildApiV3Url(`${BASE}/rules/packs`),
            { template_id: templateId, pack_id: packId },
            requestConfig
        )
        .then(response => response.data);

/* Starting a rule set for a company that has none. Unlike a form, it begins
   empty: a company's rules say what that company wants done differently, and
   until somebody writes one the answer is nothing. Creating it therefore changes
   no verdict, even after it is published. */

export const getCompaniesWithRules = (): Promise<number[]> =>
    axiosWithoutInterceptor
        .get<{
            companyIds: number[] | null;
        }>(buildApiV3Url(`${BASE}/rules/bound-companies`), requestConfig)
        .then(response => response.data?.companyIds ?? []);

export const createCompanyPack = (
    companyId: number,
    displayName: string,
    packId?: string
): Promise<MsRuleDraftSaved> =>
    axiosWithoutInterceptor
        .post<MsRuleDraftSaved>(
            buildApiV3Url(`${BASE}/rules/packs/company`),
            { company_id: companyId, display_name: displayName, pack_id: packId },
            requestConfig
        )
        .then(response => response.data);

/* Rolling back. Re-publishes an earlier version's content as a *new* version —
   history is never rewritten, because the version a stored verdict names has to
   keep meaning what it meant. */

export const restoreRuleVersion = (
    packId: string,
    fromVersion: number
): Promise<MsRuleDraftSaved> =>
    axiosWithoutInterceptor
        .post<MsRuleDraftSaved>(
            buildApiV3Url(`${BASE}/rules/packs/${encodeURIComponent(packId)}/restore`),
            { from_version: fromVersion },
            requestConfig
        )
        .then(response => response.data);

export const getRuleDiff = (
    packId: string,
    before = "live",
    after = "draft"
): Promise<MsRuleDiff> =>
    axiosWithoutInterceptor
        .get<MsRuleDiff>(
            buildApiV3Url(
                `${BASE}/rules/packs/${encodeURIComponent(packId)}/diff${buildQuery({
                    before,
                    after,
                })}`
            ),
            requestConfig
        )
        .then(response => response.data);

/* ── Seller-name aliases ────────────────────────────────────────────────────
 * A far simpler write path than the rules pack: one account, one document, two
 * string lists. Same draft → validate → publish lifecycle and the same
 * second-administrator approval, carried by ECM's proxy at
 * `managed-service/seller-aliases/*` alongside `managed-service/rules/*`. */

const sellerAliasesUrl = (accountId: number, resource: string): string =>
    buildApiV3Url(`${BASE}/seller-aliases/${encodeURIComponent(String(accountId))}/${resource}`);

export const getSellerAliasAccounts = (): Promise<MsSellerAliasPackSummary[]> =>
    axiosWithoutInterceptor
        .get<
            MsSellerAliasPackSummary[]
        >(buildApiV3Url(`${BASE}/seller-aliases/accounts`), requestConfig)
        .then(response => response.data ?? []);

export const getSellerAliasDocument = (accountId: number): Promise<MsSellerAliasDocument> =>
    axiosWithoutInterceptor
        .get<MsSellerAliasDocument>(sellerAliasesUrl(accountId, "document"), requestConfig)
        .then(response => response.data);

export const getSellerAliasHistory = (accountId: number): Promise<MsSellerAliasVersion[]> =>
    axiosWithoutInterceptor
        .get<MsSellerAliasVersion[]>(sellerAliasesUrl(accountId, "history"), requestConfig)
        .then(response => response.data ?? []);

export const validateSellerAliasDraft = (
    accountId: number,
    aliases: string[],
    dontAccept: string[]
): Promise<MsSellerAliasValidation> =>
    axiosWithoutInterceptor
        .post<MsSellerAliasValidation>(
            sellerAliasesUrl(accountId, "validate"),
            { aliases, dontAccept },
            requestConfig
        )
        .then(response => response.data);

export const saveSellerAliasDraft = (
    accountId: number,
    aliases: string[],
    dontAccept: string[],
    options: { accountName?: string | null; note?: string } = {}
): Promise<MsSellerAliasDocument> =>
    axiosWithoutInterceptor
        .put<MsSellerAliasDocument>(
            sellerAliasesUrl(accountId, "draft"),
            { aliases, dontAccept, accountName: options.accountName, note: options.note },
            requestConfig
        )
        .then(response => response.data);

export const discardSellerAliasDraft = (accountId: number): Promise<void> =>
    axiosWithoutInterceptor
        .delete(sellerAliasesUrl(accountId, "draft"), requestConfig)
        .then(() => undefined);

export const publishSellerAliasDraft = (accountId: number): Promise<MsSellerAliasDocument> =>
    axiosWithoutInterceptor
        .post<MsSellerAliasDocument>(sellerAliasesUrl(accountId, "publish"), {}, requestConfig)
        .then(response => response.data);

export const restoreSellerAliasVersion = (
    accountId: number,
    versionNo: number
): Promise<MsSellerAliasDocument> =>
    axiosWithoutInterceptor
        .post<MsSellerAliasDocument>(
            sellerAliasesUrl(accountId, "restore"),
            { versionNo },
            requestConfig
        )
        .then(response => response.data);

export const getSellerAliasVersion = (
    accountId: number,
    versionNo: number
): Promise<Record<string, unknown>> =>
    axiosWithoutInterceptor
        .get<
            Record<string, unknown>
        >(sellerAliasesUrl(accountId, `versions/${encodeURIComponent(String(versionNo))}`), requestConfig)
        .then(response => response.data);

export const getSellerAliasDiff = (
    accountId: number,
    from: number,
    to: number
): Promise<MsSellerAliasDiff> =>
    axiosWithoutInterceptor
        .get<MsSellerAliasDiff>(
            buildApiV3Url(
                `${BASE}/seller-aliases/${encodeURIComponent(String(accountId))}/diff${buildQuery({
                    from,
                    to,
                })}`
            ),
            requestConfig
        )
        .then(response => response.data);

/** Starting a document for an account that has none yet. */
export const createSellerAliasPack = (
    accountId: number,
    accountName: string | null,
    aliases: string[],
    dontAccept: string[]
): Promise<MsSellerAliasDocument> =>
    axiosWithoutInterceptor
        .post<MsSellerAliasDocument>(
            buildApiV3Url(`${BASE}/seller-aliases`),
            { accountId, accountName, aliases, dontAccept },
            requestConfig
        )
        .then(response => response.data);
